import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import axiosApiNoAuth from '../axiosApiNoAuth';
import { dataNoticeCollapsedAtom } from '../../shared/state/atoms';
import { VersionContext } from '../../context/VersionContext';

/**
 * DataNotice Component
 *
 * Displays a collapsible data notice with version info and warning message.
 * Used on Map Explorer and Dashboard pages.
 *
 * @param {Object} props
 * @param {string} props.variant - Display context: 'map' or 'dashboard'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.dataType - Type of data to fetch (default: 'taxdata')
 */
const DataNotice = ({
  variant = 'map',
  className = '',
  dataType = 'taxdata'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useAtom(dataNoticeCollapsedAtom);
  const versionContext = useContext(VersionContext);
  const appVersion = versionContext?.version;

  const warningMessage = "We have observed an unexpected decline in employment in 2025. Users are advised to interpret 2025 figures with caution and to focus on trends up to 2024.";

  // Get the page-specific version from localStorage (same key format as RouteChangeListener)
  const getPageVersion = () => {
    const normalizedPath = location.pathname === "/" ? "home" : location.pathname.replace(/\/$/, "").replace(/\//g, "_");
    const pageVersionKey = `page_v_${normalizedPath}`;
    return localStorage.getItem(pageVersionKey) || appVersion;
  };

  const pageVersion = getPageVersion();

  // Format the build date from the version (format: timestamp-hash)
  const formatBuildDate = (versionString) => {
    if (!versionString) return null;
    try {
      const timestamp = parseInt(versionString.split('-')[0], 10);
      if (isNaN(timestamp)) return null;
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  const buildDate = formatBuildDate(pageVersion);

  const handleVersionClick = () => {
    navigate('/download-data-filter-form');
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axiosApiNoAuth.get(`/api/dataversion/current/?type=${dataType}`);

        if (response.data && response.data.version_display) {
          setVersion(response.data.version_display);
        } else {
          throw new Error('Invalid version data format');
        }
      } catch (err) {
        console.error('[DataNotice] Error fetching data version:', err);
        setError(err.message);
        setVersion(null);
      } finally {
        setLoading(false);
      }
    };

    fetchVersion();
  }, [dataType]);

  // Link icon SVG
  const LinkIcon = () => (
    <svg
      className="w-3 h-3 inline-block mr-1"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );

  // Warning triangle icon SVG
  const WarningIcon = () => (
    <svg
      className="w-3 h-3 inline-block mr-1 text-amber-600"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        d="M12 2L1 21h22L12 2zm0 3.83L19.53 19H4.47L12 5.83zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"
      />
    </svg>
  );

  // Chevron icon for collapse/expand
  const ChevronIcon = ({ isUp }) => (
    <svg
      className={`w-4 h-4 transition-transform pointer-events-none ${isUp ? 'rotate-180' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );

  // Base container classes - differs slightly between map and dashboard variants
  const containerClasses = variant === 'dashboard'
    ? `fixed bottom-0 right-0 m-6 z-50 ${className}`
    : className;

  // Box styling with orange border (1px)
  const boxClasses = "bg-white border border-ccre-orange-900 shadow-sm";

  if (loading) {
    return (
      <div className={containerClasses}>
        <div className={`${boxClasses} p-4 w-[250px]`}>
          <span className="text-xs text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !version) {
    return (
      <div className={containerClasses}>
        <div className={`${boxClasses} p-4 w-[250px]`}>
          <span className="text-xs text-red-500">Error loading data notice</span>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className={`${boxClasses} w-[250px]`}>
        {/* Header - always visible, clickable to toggle */}
        <div
          className={`flex justify-between items-center cursor-pointer select-none ${isCollapsed ? 'py-2 px-4' : 'p-4'}`}
          onClick={toggleCollapse}
        >
          <span className="font-bold text-xs">Data Notice</span>
          <ChevronIcon isUp={isCollapsed} />
        </div>

        {/* Content - collapsible */}
        {!isCollapsed && (
          <div className="px-4 pb-4 text-xs">
            {/* Version section */}
            <div className="mb-2">
              <span
                onClick={handleVersionClick}
                className="cursor-pointer hover:underline inline-flex items-center"
              >
                <LinkIcon />
                <span className="font-semibold">Version:</span>
              </span>
              <span className="ml-1 text-ccre-orange-900">{version}</span>
            </div>

            {/* Warning section */}
            <div>
              <span className="font-semibold inline-flex items-center">
                <WarningIcon />
                Warning:
              </span>
              <span className="ml-1">{warningMessage}</span>
            </div>

            {/* Build date section */}
            {buildDate && (
              <div className="mt-2 pt-2 border-t border-gray-200 text-gray-500">
                <span className="font-semibold">Page Build:</span>
                <span className="ml-1">{buildDate}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataNotice;
